import {
  CanvasPractitionersBundle,
  useAuth,
  useCanvasPractitionersQuery,
  useLinkScriptSurePrescriberId,
  useTellescopeInfiniteQuery,
  useUsersMap,
} from "@tellescope/tanstack-query";
import { LabeledField, StateCredentialInfo } from "@tellescope/types-models";
import { US_STATES } from "@tellescope/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EditUserForm } from "../useEditUserForm";

export interface UseProfileTabContentProps {
  userId?: string;
  form: EditUserForm;
  credentialedStates: StateCredentialInfo[];
  templateFields: LabeledField[];
}

export const useProfileTabContent = ({
  userId,
  form,
  credentialedStates,
  templateFields,
}: UseProfileTabContentProps) => {
  const { t } = useTranslation("settings");
  const { selectedOrganization } = useAuth();
  const hasScriptSure = !!selectedOrganization?.scriptSurePractices?.length;

  const userMap = useUsersMap();
  const user = userId ? userMap[userId] : undefined;

  const statesWithCredentials = useMemo(() => {
    const selectedStates = credentialedStates.reduce<Set<string>>((acc, curr) => {
      acc.add(curr.state);
      return acc;
    }, new Set<string>());
    return US_STATES.filter(({ value }) => selectedStates.has(value));
  }, [credentialedStates]);

  const locationsQuery = useTellescopeInfiniteQuery({
    model: "appointment_locations",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat().map((location) => ({ value: location.id, label: location.title })),
  });
  const canvasPractitionersQuery = useCanvasPractitionersQuery({
    enabled: selectedOrganization?.hasConnectedCanvas,
    select: (data: { data: CanvasPractitionersBundle }) =>
      data.data.entry?.map((entry) => {
        const name = entry.resource.name?.[0]?.text;
        return {
          value: entry.resource.id,
          label: name ? `${name} (${entry.resource.id})` : entry.resource.id,
        };
      }) ?? [],
  });
  const { linkScriptSurePrescriberId, isPending: scriptsureLinkIsPending } = useLinkScriptSurePrescriberId({
    onLookupError: (type, originalErrorString) => {
      switch (type) {
        case "user-not-found":
          toast.error(t("Tellescope user not found"));
          return;
        case "user-missing-email-address":
          toast.error(t("Tellescope user must have an email address to link to ScriptSure"));
          return;
        case "org-missing-scriptsure":
          toast.error(t("ScriptSure is not set up for this organization"));
          return;
        case "no-scriptsure-user-found":
          toast.error(t("No ScriptSure user found for this user's email address"));
          return;
        default:
          toast.error(originalErrorString || t("An unknown error occurred"));
      }
    },
  });

  const selectedLocationOption = useMemo(
    () => locationsQuery.data?.find((option) => option.value === form.values.defaultLocationId),
    [form.values.defaultLocationId, locationsQuery.data]
  );

  const selectedCanvasOption = useMemo(() => {
    if (!form.values.canvasId) return undefined;
    return (
      canvasPractitionersQuery.data?.find((option) => option.value === form.values.canvasId) ?? {
        value: form.values.canvasId,
        label: form.values.canvasId,
      }
    );
  }, [form.values.canvasId, canvasPractitionersQuery.data]);

  const { suffixOptions, templateFieldKeyOptions } = useMemo(() => {
    const { suffixes, templateFieldKeys } = Object.values(userMap).reduce<{
      suffixes: Set<string>;
      templateFieldKeys: Set<string>;
    }>(
      (acc, curr) => {
        curr.suffixes?.forEach((suffix) => {
          if (suffix) acc.suffixes.add(suffix);
        });
        curr.templateFields?.forEach((field) => {
          if (field.field) acc.templateFieldKeys.add(field.field);
        });
        return acc;
      },
      { suffixes: new Set<string>(), templateFieldKeys: new Set<string>() }
    );
    form.values.suffixes?.forEach((suffix) => {
      if (suffix) suffixes.add(suffix);
    });
    return {
      suffixOptions: Array.from(suffixes).map((suffix) => ({
        value: suffix,
        label: suffix,
      })),
      templateFieldKeyOptions: Array.from(templateFieldKeys).reduce<{ value: string; label: string }[]>(
        (acc, templateFieldKey) => {
          if (!templateFields.some((field) => field.field !== templateFieldKey)) {
            acc.push({
              value: templateFieldKey,
              label: templateFieldKey,
            });
          }
          return acc;
        },
        []
      ),
    };
  }, [userMap, form.values.suffixes, templateFields]);

  return {
    t,
    user,
    hasScriptSure,
    hasConnectedCanvas: !!selectedOrganization?.hasConnectedCanvas,
    hasConnectedPaubox: !!selectedOrganization?.hasConnectedPaubox,
    locationOptions: locationsQuery.data ?? [],
    selectedLocationOption,
    canvasOptions: canvasPractitionersQuery.data ?? [],
    selectedCanvasOption,
    linkScriptSurePrescriberId,
    scriptsureLinkIsPending,
    suffixOptions,
    templateFieldKeyOptions,
    statesWithCredentials,
  };
};
